import { useTranslation } from 'react-i18next'
import GoalStatusLabel from './goal-status-label'
import GoalStatusDescription from './goal-status-description'
import { useContext } from 'react'
import { HomeContext } from '../../../state/home-context'
import WidgetLoading from 'components/common/cards/widget-loading'
import {
  type CurrentWeekGoalIndicatorsResponse,
  useApiGetCurrentWeekGoalIndicators
} from 'core/api/current-week/useApiGetCurrentWeekGoalIndicators'
import WidgetErrorCard from 'components/common/cards/widget-error-card'
import Gauge from 'components/common/gauge/gauge'
import { isHomeApiEnabled } from 'core/api/home/home-api-utils'

const gaugeChartData = [
  { name: 'BAD', value: 1 },
  { name: 'MEDIUM', value: 1 },
  { name: 'GOOD', value: 1 }
]

const getMessageErrorTranslationKey = (response: CurrentWeekGoalIndicatorsResponse) => {
  if (response?.realizedOnError === true) {
    return 'general.homepage.weekly-goal.unavailable-realized'
  }
  if (response?.forecastOnError === true) {
    return 'general.homepage.weekly-goal.forecast-undefined'
  }
  return 'general.homepage.unavailable'
}

interface WeeklyGoalProps {
  isCurrentWeek: boolean
}

const WeeklyGoal = ({ isCurrentWeek }: WeeklyGoalProps) => {
  const { t } = useTranslation()
  const { aggregation, teamId, channel, storeId, store } = useContext(HomeContext)
  const currency = store?.currency ?? ''
  const { data, isLoading } = useApiGetCurrentWeekGoalIndicators(
    { storeId, teamId, channel, aggregation },
    isCurrentWeek && isHomeApiEnabled(storeId, teamId, aggregation)
  )

  if (isLoading) {
    return (
      <WidgetLoading
        className="mg-weekly-goal mg-weekly-goal-loading"
        dataTestId="weekly-goal-loading"
        skeletonCount={3}
      />
    )
  }

  if (isCurrentWeek && (data?.probable == null || data?.forecasted == null || data?.goalStatus == null)) {
    return (
      <WidgetErrorCard
        titleKey="general.homepage.weekly-goal.title"
        messageKey={getMessageErrorTranslationKey(data)}
        testId="weekly-goal-unavailable"
      />
    )
  }

  return (
    <div className="mg-weekly-goal">
      <div className="mg-weekly-goal-text">
        <div className="vp-caption">{t('general.homepage.weekly-goal.title')}</div>
        <div className="mg-weekly-goal-status">
          {isCurrentWeek ? (
            <>
              <GoalStatusLabel goalStatus={data?.goalStatus} />
              <GoalStatusDescription
                probable={data?.probable}
                forecasted={data?.forecasted}
                goalStatus={data?.goalStatus}
                currency={currency}
              />
            </>
          ) : (
            <div className="vp-body-s mg-weekly-goal-status-description">
              {t('general.homepage.weekly-goal.unavailable-not-current-week')}
            </div>
          )}
        </div>
      </div>
      {isCurrentWeek && <Gauge chartData={gaugeChartData} currentName={data?.goalStatus} />}
    </div>
  )
}

export default WeeklyGoal
